import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { 
  Shield,
  Lock,
  Key,
  Users,
  Eye,
  Database,
  FileText,
  Server,
  Globe,
  CheckCircle2,
  AlertTriangle,
  Download,
  ShieldCheck,
  Fingerprint,
  UserCheck,
  History,
  HardDrive,
  Cloud,
  Wifi,
  RefreshCw
} from 'lucide-react';

export default function SecurityDocPage() {
  const securityFeatures = [
    {
      category: 'Authentication & Access Control',
      icon: Lock,
      color: 'text-red-500',
      bgColor: 'bg-red-500/10',
      features: [
        {
          name: 'JWT (JSON Web Token) Authentication',
          description: 'Industry-standard token-based authentication for secure user sessions',
          details: [
            'Tokens expire after 24 hours for security',
            'Tokens are signed using HS256 algorithm',
            'Secret key stored securely in environment variables',
            'Token contains user ID, role, and organization ID'
          ],
          icon: Key
        },
        {
          name: 'Password Security',
          description: 'Passwords are never stored in plain text',
          details: [
            'Passwords hashed using bcrypt algorithm',
            'Salt automatically generated for each password',
            'Original passwords cannot be recovered',
            'Password verification done server-side only'
          ],
          icon: Fingerprint
        },
        {
          name: 'Role-Based Access Control (RBAC)',
          description: '5 distinct user roles with specific permissions',
          details: [
            'Super Admin: Full platform control, manage all tenants',
            'Org Admin: Full organization access, user management',
            'Inventory Manager: Create items, handle all transactions',
            'Warehouse Staff: Limited to assigned operations',
            'Auditor: Read-only access for compliance'
          ],
          icon: UserCheck
        },
        {
          name: 'Session Management',
          description: 'Secure handling of user sessions',
          details: [
            'Tokens stored in browser memory (not localStorage for security)',
            'Automatic logout on token expiration',
            'No session data stored on server (stateless)',
            'Protected routes require valid authentication'
          ],
          icon: Users
        }
      ]
    },
    {
      category: 'Data Protection',
      icon: Database,
      color: 'text-blue-500',
      bgColor: 'bg-blue-500/10',
      features: [
        {
          name: 'Multi-Tenant Data Isolation',
          description: 'Complete separation of data between organizations',
          details: [
            'Each organization has unique organization_id',
            'All database queries filtered by organization_id',
            'Users can only access their organization\'s data',
            'No cross-tenant data leakage possible'
          ],
          icon: Database
        },
        {
          name: 'Input Validation',
          description: 'All user inputs are validated before processing',
          details: [
            'Pydantic models validate all API inputs',
            'Email format validation on registration',
            'Type checking prevents injection attacks',
            'Sanitization of special characters'
          ],
          icon: ShieldCheck
        },
        {
          name: 'Secure API Design',
          description: 'RESTful API with security best practices',
          details: [
            'All sensitive endpoints require authentication',
            'HTTP-only secure communication (HTTPS)',
            'CORS configuration to prevent unauthorized access',
            'Rate limiting to prevent abuse'
          ],
          icon: Server
        }
      ]
    },
    {
      category: 'Audit & Compliance',
      icon: Eye,
      color: 'text-green-500',
      bgColor: 'bg-green-500/10',
      features: [
        {
          name: 'Comprehensive Audit Logging',
          description: 'Every significant action is logged for accountability',
          details: [
            'Logs: User ID, action, entity, timestamp',
            'Tracks: Create, Update, Delete, Payment events',
            'Old and new values stored for changes',
            'Immutable audit trail cannot be modified'
          ],
          icon: History
        },
        {
          name: 'Stock Movement Traceability',
          description: 'Complete history of all inventory changes',
          details: [
            'Every transaction creates movement record',
            'Links to user, warehouse, item, quantity',
            'Reason/reference stored for each movement',
            'Supports regulatory compliance requirements'
          ],
          icon: FileText
        },
        {
          name: 'User Activity Tracking',
          description: 'Monitor who did what and when',
          details: [
            'Login/logout events tracked',
            'All data modifications attributed to users',
            'Audit log accessible to Org Admin and Auditor',
            'Export capability for compliance reporting'
          ],
          icon: Eye
        }
      ]
    },
    {
      category: 'Payment Security',
      icon: Shield,
      color: 'text-purple-500',
      bgColor: 'bg-purple-500/10',
      features: [
        {
          name: 'Stripe Integration',
          description: 'PCI-compliant payment processing',
          details: [
            'Card details never touch our servers',
            'Stripe handles all sensitive payment data',
            'PCI DSS Level 1 certified processor',
            'Secure checkout via HTTPS redirect'
          ],
          icon: Shield
        },
        {
          name: 'Server-Side Price Validation',
          description: 'Prevents price manipulation attacks',
          details: [
            'Prices defined only on backend',
            'Frontend cannot modify payment amounts',
            'Plan prices hardcoded in server code',
            'Validates plan_id before checkout'
          ],
          icon: Lock
        },
        {
          name: 'Payment Verification',
          description: 'Double-verification of payments',
          details: [
            'Polling mechanism confirms payment status',
            'Webhook backup for payment events',
            'Transaction records prevent duplicate processing',
            'Subscription only activates after verification'
          ],
          icon: CheckCircle2
        }
      ]
    },
    {
      category: 'Infrastructure Security',
      icon: Cloud,
      color: 'text-cyan-500',
      bgColor: 'bg-cyan-500/10',
      features: [
        {
          name: 'Cloud Hosting',
          description: 'Enterprise-grade cloud infrastructure',
          details: [
            'Hosted on secure cloud platform',
            'Automatic backups and disaster recovery',
            'DDoS protection at network level',
            'Geographic redundancy for uptime'
          ],
          icon: Cloud
        },
        {
          name: 'HTTPS Encryption',
          description: 'All data encrypted in transit',
          details: [
            'TLS 1.3 encryption for all connections',
            'SSL certificate always valid',
            'No HTTP fallback allowed',
            'Secure WebSocket connections'
          ],
          icon: Wifi
        },
        {
          name: 'Database Security',
          description: 'MongoDB with security best practices',
          details: [
            'Database not exposed to public internet',
            'Connection via internal network only',
            'Indexed queries prevent full scans',
            'Regular security patches applied'
          ],
          icon: HardDrive
        }
      ]
    },
    {
      category: 'Application Security',
      icon: ShieldCheck,
      color: 'text-orange-500',
      bgColor: 'bg-orange-500/10',
      features: [
        {
          name: 'Environment Variables',
          description: 'Secrets stored securely',
          details: [
            'API keys in environment variables',
            'No secrets in source code',
            'Different keys for dev/production',
            '.env files excluded from version control'
          ],
          icon: Key
        },
        {
          name: 'Dependency Management',
          description: 'Secure package management',
          details: [
            'Regular dependency updates',
            'Security vulnerability scanning',
            'Locked package versions',
            'No deprecated packages'
          ],
          icon: RefreshCw
        },
        {
          name: 'Error Handling',
          description: 'Secure error responses',
          details: [
            'Generic error messages to users',
            'Detailed logs on server only',
            'No stack traces exposed',
            'Graceful degradation on failures'
          ],
          icon: AlertTriangle
        }
      ]
    }
  ];

  const complianceMatrix = [
    { standard: 'Authentication', requirement: 'Secure login mechanism', status: 'Implemented', method: 'JWT + bcrypt' },
    { standard: 'Authorization', requirement: 'Role-based access', status: 'Implemented', method: 'RBAC with 5 roles' },
    { standard: 'Data Encryption', requirement: 'Encrypt data in transit', status: 'Implemented', method: 'TLS 1.3 / HTTPS' },
    { standard: 'Password Storage', requirement: 'Hash passwords', status: 'Implemented', method: 'bcrypt with salt' },
    { standard: 'Audit Trail', requirement: 'Log all changes', status: 'Implemented', method: 'Audit log collection' },
    { standard: 'Data Isolation', requirement: 'Tenant separation', status: 'Implemented', method: 'organization_id filtering' },
    { standard: 'Payment Security', requirement: 'PCI compliance', status: 'Implemented', method: 'Stripe (Level 1)' },
    { standard: 'Input Validation', requirement: 'Validate all inputs', status: 'Implemented', method: 'Pydantic models' }
  ];

  const handleDownloadPDF = () => {
    window.print();
  };

  return (
    <div className="space-y-8 print:space-y-4" data-testid="security-doc-page">
      {/* Header */}
      <div className="flex items-center justify-between print:block">
        <div>
          <h1 className="text-heading-black text-3xl lg:text-4xl text-[#0A0A0A] tracking-tight">SECURITY DOCUMENTATION</h1>
          <p className="text-[#0A0A0A]/60 mt-1">Complete security overview of JamInventra</p>
        </div>
        <Button onClick={handleDownloadPDF} className="bg-[#007AFF] hover:bg-[#0066D6] print:hidden">
          <Download className="w-4 h-4 mr-2" />
          Print / Save PDF
        </Button>
      </div>

      {/* Security Overview */}
      <Card className="border-2 border-[#34C759] shadow-none bg-[#34C759]/5 print:border print:bg-white">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-[#34C759]">
            <ShieldCheck className="w-6 h-6" />
            Security Status: PROTECTED
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-white rounded-lg">
              <p className="text-2xl font-bold text-[#0A0A0A]">JWT</p>
              <p className="text-sm text-[#0A0A0A]/60">Authentication</p>
            </div>
            <div className="text-center p-4 bg-white rounded-lg">
              <p className="text-2xl font-bold text-[#0A0A0A]">bcrypt</p>
              <p className="text-sm text-[#0A0A0A]/60">Password Hashing</p>
            </div>
            <div className="text-center p-4 bg-white rounded-lg">
              <p className="text-2xl font-bold text-[#0A0A0A]">HTTPS</p>
              <p className="text-sm text-[#0A0A0A]/60">Encryption</p>
            </div>
            <div className="text-center p-4 bg-white rounded-lg">
              <p className="text-2xl font-bold text-[#0A0A0A]">RBAC</p>
              <p className="text-sm text-[#0A0A0A]/60">Access Control</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Security Features */}
      {securityFeatures.map((section, idx) => (
        <Card key={idx} className="border border-[#E5E5EA] shadow-none print:break-inside-avoid">
          <CardHeader className="border-b border-[#E5E5EA] pb-4">
            <CardTitle className="flex items-center gap-3">
              <div className={`w-12 h-12 ${section.bgColor} rounded-lg flex items-center justify-center`}>
                <section.icon className={`w-6 h-6 ${section.color}`} />
              </div>
              <h2 className="text-heading text-xl text-[#0A0A0A]">{section.category}</h2>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-6">
              {section.features.map((feature, fidx) => (
                <div key={fidx} className="border-l-4 border-[#E5E5EA] pl-4">
                  <div className="flex items-start gap-3 mb-2">
                    <div className={`w-8 h-8 ${section.bgColor} rounded-md flex items-center justify-center flex-shrink-0`}>
                      <feature.icon className={`w-4 h-4 ${section.color}`} />
                    </div>
                    <div>
                      <h4 className="font-semibold text-[#0A0A0A]">{feature.name}</h4>
                      <p className="text-sm text-[#0A0A0A]/60">{feature.description}</p>
                    </div>
                  </div>
                  <ul className="ml-11 space-y-1">
                    {feature.details.map((detail, didx) => (
                      <li key={didx} className="flex items-center gap-2 text-sm text-[#0A0A0A]/70">
                        <CheckCircle2 className="w-3 h-3 text-[#34C759] flex-shrink-0" />
                        {detail}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ))}

      {/* Compliance Matrix */}
      <Card className="border border-[#E5E5EA] shadow-none print:break-inside-avoid">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#007AFF]" />
            Security Compliance Matrix
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#E5E5EA]">
                  <th className="text-left py-3 px-4 font-semibold text-[#0A0A0A]">Standard</th>
                  <th className="text-left py-3 px-4 font-semibold text-[#0A0A0A]">Requirement</th>
                  <th className="text-left py-3 px-4 font-semibold text-[#0A0A0A]">Status</th>
                  <th className="text-left py-3 px-4 font-semibold text-[#0A0A0A]">Implementation</th>
                </tr>
              </thead>
              <tbody>
                {complianceMatrix.map((item, idx) => (
                  <tr key={idx} className="border-b border-[#E5E5EA] last:border-0">
                    <td className="py-3 px-4 font-medium text-[#0A0A0A]">{item.standard}</td>
                    <td className="py-3 px-4 text-[#0A0A0A]/70">{item.requirement}</td>
                    <td className="py-3 px-4">
                      <Badge className="bg-[#34C759]/10 text-[#34C759]">
                        <CheckCircle2 className="w-3 h-3 mr-1" />
                        {item.status}
                      </Badge>
                    </td>
                    <td className="py-3 px-4 font-mono text-xs text-[#0A0A0A]/70">{item.method}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Technical Summary */}
      <Card className="border border-[#E5E5EA] shadow-none bg-gradient-to-br from-[#007AFF]/5 to-transparent print:bg-white">
        <CardContent className="p-6">
          <h3 className="text-heading text-lg text-[#0A0A0A] mb-4">Security Architecture Summary</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <h4 className="font-semibold text-[#0A0A0A] mb-2">Authentication Layer</h4>
              <ul className="text-sm text-[#0A0A0A]/70 space-y-1">
                <li>• JWT tokens (HS256)</li>
                <li>• bcrypt password hashing</li>
                <li>• 24-hour token expiration</li>
                <li>• Stateless session management</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-[#0A0A0A] mb-2">Authorization Layer</h4>
              <ul className="text-sm text-[#0A0A0A]/70 space-y-1">
                <li>• RBAC with 5 roles</li>
                <li>• Route-level protection</li>
                <li>• Organization data isolation</li>
                <li>• Permission decorators</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-[#0A0A0A] mb-2">Data Layer</h4>
              <ul className="text-sm text-[#0A0A0A]/70 space-y-1">
                <li>• MongoDB with indexes</li>
                <li>• Pydantic validation</li>
                <li>• Audit logging</li>
                <li>• Encrypted connections</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
